import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown } from '@carbon/react';
import { Close, Maximize } from '@carbon/react/icons';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { computeTypeCurveValues } from './compute-probit-type-curve-values';
import { MineralType } from 'types/minerals';
import { XAxisType } from 'components/well-page-content/types';
import { MultiWellVolumeChart } from 'components/charts';
import {
  type MultiWellSeriesStyle,
  type MultiWellProductionData,
} from 'components/charts/multi-well-volume-chart';
import { ProbitChart, type YAxisMode } from 'components/charts/probit-chart';
import { XAxisSwitcher } from 'components/well-page-content/decline-curve-analysis/x-axis-switcher';
import type {
  TypeCurveRatioRecord,
  TypeCurveVolumeRecord,
} from 'types/api-responses';
import type {
  TypeCurveProbitMetric,
  TypeCurveProbitRequest,
  TypeCurveProbitResponse,
} from 'types/type-curve-probit';
import type {
  TypeCurveVolumes,
  AggregationCache,
  WellCountData,
} from '../hooks';
import {
  typeCurveProbitMutationAtoms,
  createTypeCurveProbitMutationAtom,
  TYPE_CURVE_PROBIT_URL,
  tcStreamAggregationAtom,
  tcForecastOverrideAtom,
  tcLateralLengthFtAtom,
  probitLastDispatchKey,
  tcProbitOverrideAtoms,
  type TcForecastOverride,
} from '../atoms';
import type { FilteredWell } from '../types';
import styles from './tc-dca-chart-grid.module.scss';

// ---------------------------------------------------------------------------
// Stream colors — kept in sync with type-curve-volume-charts.tsx
// ---------------------------------------------------------------------------

const STREAM_COLORS: Record<string, string> = {
  oil: '#42be65',
  gas: '#fa4d56',
  water: '#4589ff',
  gor: '#FF7EB6',
  wor: '#82CFFF',
  wgr: '#BE95FF',
  cgr: '#d2a106',
  watercut: '#0062ff',
};

// Per-stream seriesStyle objects — module-level for stable references.
// Type curve mirrors the aggregation colour & solid style; forecast is black dashed.
const STREAM_SERIES_STYLES: Record<string, MultiWellSeriesStyle> =
  Object.fromEntries(
    Object.entries(STREAM_COLORS).map(([id, color]) => [
      id,
      {
        aggregation: { color },
        typeCurve: { color, style: 'dash' as const },
        forecast: { color: '#161616', style: 'dash' as const },
      },
    ])
  );

// Default ratio stream per mineral type.
const DEFAULT_RATIO_STREAM: Record<
  MineralType,
  'gor' | 'cgr' | 'wor' | 'wgr' | 'watercut'
> = {
  oil: 'gor',
  gas: 'cgr',
  water: 'wor',
};

// Ratio dropdown items.
const RATIO_OPTIONS: Array<{
  id: 'gor' | 'wor' | 'wgr' | 'cgr' | 'watercut';
  text: string;
}> = [
  { id: 'gor', text: 'GOR' },
  { id: 'wor', text: 'WOR' },
  { id: 'wgr', text: 'WGR' },
  { id: 'cgr', text: 'CGR' },
  { id: 'watercut', text: 'Watercut' },
];

// Chart type labels for headers.
const CHART_TYPE_LABELS: Record<string, string> = {
  monthly: 'Monthly Volumes',
  logarithmic: 'Log Log Volumes',
  cumulative: 'Cumulative Volumes',
  ratio: 'Ratio',
};

const AGGREGATION_OPTIONS: Array<{ id: string; text: string }> = [
  { id: 'p10', text: 'P10' },
  { id: 'p50', text: 'P50' },
  { id: 'p90', text: 'P90' },
  { id: 'arithmeticMean', text: 'Arithmetic Avg' },
  { id: 'geometricMean', text: 'Geometric Avg' },
];

// Stable selectedSeries arrays per mineral stream — module-level.
const SELECTED_SERIES_BY_STREAM: Record<MineralType, MineralType[]> = {
  oil: ['oil'],
  gas: ['gas'],
  water: ['water'],
};

// Probit Y-axis mode options.
const Y_MODE_ITEMS: Array<{ id: YAxisMode; text: string }> = [
  { id: 'percentile', text: 'Percentile' },
  { id: 'probit', text: 'Probit' },
];

// Probit X-axis metric options (simplified for DCA modal — EUR-based only).
const PROBIT_METRIC_ITEMS: Array<{
  id: TypeCurveProbitMetric;
  text: string;
}> = [
  { id: 'scaled_eur', text: 'Scaled EUR' },
  { id: 'eur', text: 'EUR' },
  { id: 'eur_per_ft', text: 'EUR per ft' },
];

function itemToString<T extends string>(
  item: { id: T; text: string } | null
): string {
  return item ? item.text : '';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TcDcaChartGridProps {
  stream: MineralType;
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  wells: FilteredWell[];
  showTypeCurve?: boolean;
  volumeDisplayMode?: 'monthly' | 'daily';
  forecastData?: {
    volume: TypeCurveVolumeRecord[];
    cumulative: TypeCurveVolumeRecord[];
    ratio: TypeCurveRatioRecord[];
    materialBalanceTime: TypeCurveVolumeRecord[];
  } | null;
  showForecast?: boolean;
  /** Pre-fetched production well data — avoids duplicate API calls when provided. */
  productionWells?: MultiWellProductionData[];
  /** Loading state for production data (used when productionWells is provided). */
  productionLoading?: boolean;
  /** Pre-fetched type curve volumes — avoids duplicate API calls when provided. */
  typeCurveData?: TypeCurveVolumes | null;
  /** Pre-fetched aggregation cache — avoids duplicate API calls when provided. */
  aggregationCache?: AggregationCache;
  /** Pre-fetched well count data — avoids duplicate API calls when provided. */
  wellCounts?: WellCountData | null;
  /** Local forecast override for probit — used instead of reading global atom when provided. */
  localForecastOverride?: TcForecastOverride | null;
  /** Whether a bulk forecast mutation is in-flight (debounced DCA form changes). */
  forecastLoading?: boolean;
  /** Called when a new probit result arrives in modal mode (modal captures for save). */
  onProbitResult?: (data: TypeCurveProbitResponse) => void;
  /** DCA scenario ID used only for the probit chart. When provided, the probit
   *  dispatch uses this instead of `dcaScenarioId`. This allows the modal to
   *  change the scenario for volume charts without affecting probit. */
  probitDcaScenarioId?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TcDcaChartGrid = ({
  stream,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  wells,
  showTypeCurve: showTypeCurveProp = true,
  volumeDisplayMode,
  forecastData,
  showForecast = false,
  productionWells: productionWellsProp,
  productionLoading: productionLoadingProp,
  typeCurveData: typeCurveDataProp,
  aggregationCache: aggregationCacheProp,
  wellCounts: wellCountsProp,
  localForecastOverride,
  forecastLoading = false,
  onProbitResult,
  probitDcaScenarioId,
}: TcDcaChartGridProps) => {
  // Top-left chart type toggle: monthly vs logarithmic.
  const [chartType, setChartType] = useState<'monthly' | 'logarithmic'>(
    'monthly'
  );
  // Shared x-axis for monthly + cumulative charts.
  const [volumeXAxisType, setVolumeXAxisType] = useState<XAxisType>('count');
  // Independent x-axis for the ratio chart.
  const [ratioXAxisType, setRatioXAxisType] = useState<XAxisType>('count');
  // Selected ratio stream.
  const [selectedRatio, setSelectedRatio] = useState<
    'gor' | 'wor' | 'wgr' | 'cgr' | 'watercut'
  >(DEFAULT_RATIO_STREAM[stream]);
  // Cross-chart hover state.
  const [highlightedUwi, setHighlightedUwi] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  // In-place chart expansion: which chart fills the grid (null = 2x2 view).
  type ChartSlot = 'volume' | 'probit' | 'cumulative' | 'ratio';
  const [expandedChart, setExpandedChart] = useState<ChartSlot | null>(null);

  // Probit controls.
  const [probitMetric, setProbitMetric] =
    useState<TypeCurveProbitMetric>('scaled_eur');
  const [probitYMode, setProbitYMode] = useState<YAxisMode>('percentile');
  const [probitAxisSwapped, setProbitAxisSwapped] = useState(false);

  // ── Data (received via props from the modal, no hooks) ──

  const uwis = useMemo(() => wells.map((w) => w.uwi), [wells]);

  const productionWells = productionWellsProp ?? [];
  const productionLoading = productionLoadingProp ?? false;
  const typeCurveData = typeCurveDataProp ?? null;
  const aggregationCache = aggregationCacheProp ?? {};
  const wellCounts = wellCountsProp ?? null;

  const [streamAggTypes, setStreamAggTypes] = useAtom(tcStreamAggregationAtom);
  const aggregation = streamAggTypes[stream] ?? 'p50';

  const handleAggChange = useCallback(
    (type: string) => {
      setStreamAggTypes((prev) => ({ ...prev, [stream]: type }));
    },
    [setStreamAggTypes, stream]
  );

  const aggData = aggregationCache[aggregation] ?? null;
  const aggLabel =
    AGGREGATION_OPTIONS.find((o) => o.id === aggregation)?.text ?? aggregation;

  // ── Probit data fetching ──

  const effectiveProbitScenarioId = probitDcaScenarioId ?? dcaScenarioId;

  const globalForecastOverride = useAtomValue(tcForecastOverrideAtom);
  const forecastOverride =
    localForecastOverride !== undefined
      ? localForecastOverride
      : globalForecastOverride;
  const lateralLengthFt = useAtomValue(tcLateralLengthFtAtom);

  // In modal mode, dispatch to a LOCAL probit atom so intermediate/error
  // states don't leak to the main page behind the modal. The shared atom is
  // read below for display-fallback only.
  const isModalMode = localForecastOverride !== undefined;
  const localProbitAtom = useMemo(
    () => createTypeCurveProbitMutationAtom(),
    []
  );
  const probitAtom = isModalMode
    ? localProbitAtom
    : typeCurveProbitMutationAtoms[stream];

  const [{ loading: probitLoading, result: probitResult }, probitDispatch] =
    useAtom(probitAtom);

  // In modal mode the local atom starts empty. Fallback chain:
  // local result → override atom → shared mutation atom.
  const probitOverride = useAtomValue(tcProbitOverrideAtoms[stream]);
  const sharedProbitState = useAtomValue(typeCurveProbitMutationAtoms[stream]);
  const effectiveProbitResult =
    probitResult ??
    (isModalMode
      ? probitOverride
        ? { type: 'success' as const, data: probitOverride }
        : sharedProbitState.result
      : null);

  const wellMetaByUwi = useMemo(() => {
    const map: Record<string, { name?: string; qualifier?: string }> = {};
    for (const well of wells) {
      if (!well?.uwi) continue;
      map[well.uwi] = {
        name: well.name ?? undefined,
        qualifier: well.wellQualifier ?? undefined,
      };
    }
    return map;
  }, [wells]);

  const uwisKey = useMemo(() => uwis.join(','), [uwis]);

  // Stream-specific key (same approach as main-page probit). Only tracks
  // THIS stream's typeCurveValue so that e.g. oil EUR changing doesn't
  // cause the gas/water probit to re-fetch.
  const typeCurveValuesKey = useMemo(() => {
    if (!forecastOverride) return 'null';
    const tcv = computeTypeCurveValues(
      forecastOverride,
      probitMetric,
      typeCurveHeaderDetailsId,
      { lateralLengthFt }
    );
    if (!tcv) return 'null';
    const sv = tcv[stream];
    if (sv !== undefined) return String(sv);
    if (probitMetric === 'ratio') return JSON.stringify(tcv);
    return 'null';
  }, [
    forecastOverride,
    probitMetric,
    typeCurveHeaderDetailsId,
    lateralLengthFt,
    stream,
  ]);

  useEffect(() => {
    if (!typeCurveHeaderDetailsId) return;
    if (uwis.length === 0) return;

    // Shared dispatch key — if any component (main page or DCA modal) already
    // dispatched with these exact params for THIS stream, skip. In modal mode
    // we check the effective result (which falls back to the shared atom) so
    // opening the modal with unchanged params doesn't re-fetch.
    const dispatchKey =
      `${probitMetric}:::${typeCurveHeaderDetailsId}:` +
      `${effectiveProbitScenarioId}:${uwisKey}:${typeCurveValuesKey}:${lateralLengthFt}`;

    const resultToCheck = isModalMode ? effectiveProbitResult : probitResult;
    if (
      dispatchKey === probitLastDispatchKey[stream] &&
      resultToCheck?.type === 'success'
    ) {
      return;
    }

    const typeCurveValues = computeTypeCurveValues(
      forecastOverride,
      probitMetric,
      typeCurveHeaderDetailsId,
      { lateralLengthFt }
    );

    const payload: TypeCurveProbitRequest = {
      dcaScenarioId: effectiveProbitScenarioId,
      typeCurveHeaderDetailsId: typeCurveHeaderDetailsId,
      metric: probitMetric,
      uwis,
      useTcScenarioScaling:
        import.meta.env.VITE_TYPE_CURVE_SCENARIO_SCALING_ENABLED === 'true',
      ...(typeCurveValues && { typeCurveValues }),
      responseConfig: {
        includeTypeCurvePValue: true,
        includeLatestPromotedForecastOnly: true,
      },
    };

    probitLastDispatchKey[stream] = dispatchKey;
    probitDispatch({
      url: TYPE_CURVE_PROBIT_URL,
      type: 'POST',
      data: payload,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    probitMetric,
    typeCurveHeaderDetailsId,
    effectiveProbitScenarioId,
    uwisKey,
    probitDispatch,
    typeCurveValuesKey,
    lateralLengthFt,
    stream,
  ]);

  // Notify the modal parent when a new probit result arrives so it can
  // capture the data for writing to the override atom on save.
  useEffect(() => {
    if (isModalMode && probitResult?.type === 'success' && onProbitResult) {
      onProbitResult(probitResult.data);
    }
  }, [isModalMode, probitResult, onProbitResult]);

  const probitData: TypeCurveProbitResponse | null =
    effectiveProbitResult?.type === 'success'
      ? effectiveProbitResult.data
      : null;

  const probitChartData = useMemo(() => {
    if (!probitData) return null;
    const base = probitData.streams ? probitData.streams[stream] : null;
    if (!base) return null;

    const tcEntry = probitData.typeCurvePValues
      ? probitData.typeCurvePValues[stream]
      : null;
    const typeCurve = tcEntry
      ? {
          value: tcEntry.value,
          pValue: tcEntry.pValue,
          label: tcEntry.label,
        }
      : null;

    return { ...base, typeCurve, wellMetaByUwi };
  }, [probitData, stream, wellMetaByUwi]);

  const probitXAxisLabel = useMemo(() => {
    const unit = probitChartData?.unit;
    const base =
      PROBIT_METRIC_ITEMS.find((i) => i.id === probitMetric)?.text ??
      probitMetric;
    return unit ? `${base} [${unit}]` : base;
  }, [probitMetric, probitChartData]);

  // ── Stable series arrays ──

  const selectedSeries = SELECTED_SERIES_BY_STREAM[stream];
  const selectedRatioSeries = useMemo(() => [selectedRatio], [selectedRatio]);
  const seriesStyle = STREAM_SERIES_STYLES[stream];
  const ratioSeriesStyle = STREAM_SERIES_STYLES[selectedRatio];

  // ── Handlers ──

  const handleWellHover = useCallback((uwi: string | null) => {
    setHighlightedUwi(uwi);
  }, []);

  const handleVolumeXAxisChange = useCallback((val: XAxisType) => {
    setVolumeXAxisType(val);
  }, []);

  const handleRatioXAxisChange = useCallback((val: XAxisType) => {
    setRatioXAxisType(val);
  }, []);

  // ── Shared chart props ──

  const streamLabel = stream.charAt(0).toUpperCase() + stream.slice(1);
  const showTc = showTypeCurveProp && !!typeCurveData;

  // When in logarithmic mode, force x-axis to 'count'.
  const effectiveVolumeXAxis =
    chartType === 'logarithmic' ? 'count' : volumeXAxisType;
  const volumeXLinearMax = effectiveVolumeXAxis === 'count' ? 180 : undefined;
  const ratioXLinearMax = ratioXAxisType === 'count' ? 180 : undefined;

  return (
    <div
      className={classNames(styles.chartGrid, {
        [styles.chartGridExpandVolume]: expandedChart === 'volume',
        [styles.chartGridExpandProbit]: expandedChart === 'probit',
        [styles.chartGridExpandCumulative]: expandedChart === 'cumulative',
        [styles.chartGridExpandRatio]: expandedChart === 'ratio',
      })}
      ref={gridRef}
    >
      {/* ── Top-left: Monthly / Log-Log (toggle) ── */}
      <div
        className={classNames(styles.chartCell, {
          [styles.chartCellHidden]: expandedChart && expandedChart !== 'volume',
        })}
      >
        <MultiWellVolumeChart
          type={chartType}
          wells={productionWells}
          typeCurveData={typeCurveData}
          showTypeCurve={showTc}
          aggregationData={aggData}
          showAggregation={!!aggData}
          aggregationLabel={aggLabel}
          wellCountData={wellCounts}
          showWellCount={!!wellCounts}
          isLoading={productionLoading || forecastLoading}
          selectedSeries={selectedSeries}
          highlightedUwi={highlightedUwi}
          onWellHover={handleWellHover}
          xAxisType={effectiveVolumeXAxis}
          xLinearMax={volumeXLinearMax}
          autoZoomY
          seriesStyle={seriesStyle}
          volumeDisplayMode={volumeDisplayMode}
          forecastData={forecastData}
          showForecast={showForecast}
          header={
            <div className={styles.chartTitleRow}>
              <button
                type="button"
                className={classNames(styles.chartTab, {
                  [styles.chartTabActive]: chartType === 'monthly',
                })}
                onClick={() => setChartType('monthly')}
              >
                {CHART_TYPE_LABELS.monthly} &mdash; {streamLabel}
              </button>
              <button
                type="button"
                className={classNames(styles.chartTab, {
                  [styles.chartTabActive]: chartType === 'logarithmic',
                })}
                onClick={() => setChartType('logarithmic')}
              >
                {CHART_TYPE_LABELS.logarithmic} &mdash; {streamLabel}
              </button>
              <button
                type="button"
                className={styles.chartExpandButton}
                onClick={() =>
                  setExpandedChart((prev) => {
                    return prev === 'volume' ? null : 'volume';
                  })
                }
                aria-label={
                  expandedChart === 'volume' ? 'Collapse chart' : 'Expand chart'
                }
              >
                {expandedChart === 'volume' ? (
                  <Close size={16} />
                ) : (
                  <Maximize size={16} />
                )}
              </button>
            </div>
          }
          footer={
            <div className={styles.chartFooter}>
              <div className={styles.footerGroup}>
                <span className={styles.footerLabel}>X-axis:</span>
                <XAxisSwitcher
                  chartType={chartType}
                  value={effectiveVolumeXAxis}
                  onChange={handleVolumeXAxisChange}
                  className={styles.footerDropdown}
                  excludeOptions={['date']}
                  direction="top"
                />
              </div>
              <div className={styles.footerGroup}>
                <span className={styles.footerLabel}>Agg:</span>
                <Dropdown
                  id="tc-dca-aggregation"
                  titleText=""
                  label="Aggregation"
                  size="sm"
                  direction="top"
                  items={AGGREGATION_OPTIONS}
                  itemToString={(
                    item: (typeof AGGREGATION_OPTIONS)[number] | null
                  ) => (item ? item.text : '')}
                  selectedItem={
                    AGGREGATION_OPTIONS.find((o) => o.id === aggregation) ??
                    null
                  }
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem?: (typeof AGGREGATION_OPTIONS)[number] | null;
                  }) => {
                    if (selectedItem) handleAggChange(selectedItem.id);
                  }}
                  className={styles.footerDropdown}
                />
              </div>
            </div>
          }
        />
      </div>

      {/* ── Top-right: Probit Plot ── */}
      <div
        className={classNames(styles.chartCell, {
          [styles.chartCellHidden]: expandedChart && expandedChart !== 'probit',
        })}
      >
        <ProbitChart
          data={probitChartData}
          uwis={probitData?.uwis ?? []}
          yMode={probitYMode}
          axisSwapped={probitAxisSwapped}
          color={STREAM_COLORS[stream]}
          xAxisLabel={probitXAxisLabel}
          loading={probitLoading}
          highlightedUwi={highlightedUwi}
          onPointHover={handleWellHover}
          header={
            <div className={styles.chartTitleRow}>
              <span className={styles.chartTitle}>
                Probit Plot &mdash; {streamLabel}
              </span>
              <button
                type="button"
                className={styles.chartExpandButton}
                onClick={() =>
                  setExpandedChart((prev) => {
                    return prev === 'probit' ? null : 'probit';
                  })
                }
                aria-label={
                  expandedChart === 'probit'
                    ? 'Collapse probit chart'
                    : 'Expand probit chart'
                }
              >
                {expandedChart === 'probit' ? (
                  <Close size={16} />
                ) : (
                  <Maximize size={16} />
                )}
              </button>
            </div>
          }
          footer={
            <div className={styles.chartFooter}>
              <div className={styles.footerGroup}>
                <span className={styles.footerLabel}>X-axis:</span>
                <Dropdown
                  id="tc-dca-probit-metric"
                  size="sm"
                  direction="top"
                  titleText=""
                  label="Select Metric"
                  items={PROBIT_METRIC_ITEMS}
                  itemToString={itemToString}
                  selectedItem={
                    PROBIT_METRIC_ITEMS.find((i) => i.id === probitMetric) ??
                    null
                  }
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem?: (typeof PROBIT_METRIC_ITEMS)[number] | null;
                  }) => {
                    if (selectedItem) setProbitMetric(selectedItem.id);
                  }}
                  className={styles.footerDropdown}
                  disabled={probitLoading}
                />
              </div>
              <div className={styles.footerGroup}>
                <span className={styles.footerLabel}>Y-axis:</span>
                <Dropdown
                  id="tc-dca-probit-ymode"
                  size="sm"
                  direction="top"
                  titleText=""
                  label="Select Y-Axis"
                  items={Y_MODE_ITEMS}
                  itemToString={itemToString}
                  selectedItem={
                    Y_MODE_ITEMS.find((i) => i.id === probitYMode) ?? null
                  }
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem?: (typeof Y_MODE_ITEMS)[number] | null;
                  }) => {
                    if (selectedItem) setProbitYMode(selectedItem.id);
                  }}
                  className={styles.footerDropdown}
                  disabled={probitLoading}
                />
                <Button
                  kind="primary"
                  size="sm"
                  onClick={() => setProbitAxisSwapped((prev) => !prev)}
                  disabled={probitLoading}
                  className={styles.swapBtn}
                >
                  Swap
                </Button>
              </div>
            </div>
          }
        />
      </div>

      {/* ── Bottom-left: Cumulative ── */}
      <div
        className={classNames(styles.chartCell, {
          [styles.chartCellHidden]:
            expandedChart && expandedChart !== 'cumulative',
        })}
      >
        <MultiWellVolumeChart
          type="cumulative"
          wells={productionWells}
          typeCurveData={typeCurveData}
          showTypeCurve={showTc}
          aggregationData={aggData}
          showAggregation={!!aggData}
          aggregationLabel={aggLabel}
          wellCountData={wellCounts}
          showWellCount={!!wellCounts}
          isLoading={productionLoading || forecastLoading}
          selectedSeries={selectedSeries}
          highlightedUwi={highlightedUwi}
          onWellHover={handleWellHover}
          xAxisType={volumeXAxisType}
          xLinearMax={volumeXLinearMax}
          autoZoomY
          seriesStyle={seriesStyle}
          volumeDisplayMode={volumeDisplayMode}
          forecastData={forecastData}
          showForecast={showForecast}
          header={
            <div className={styles.chartTitleRow}>
              <span className={styles.chartTitle}>
                {CHART_TYPE_LABELS.cumulative} &mdash; {streamLabel}
              </span>
              <button
                type="button"
                className={styles.chartExpandButton}
                onClick={() =>
                  setExpandedChart((prev) => {
                    return prev === 'cumulative' ? null : 'cumulative';
                  })
                }
                aria-label={
                  expandedChart === 'cumulative'
                    ? 'Collapse cumulative chart'
                    : 'Expand cumulative chart'
                }
              >
                {expandedChart === 'cumulative' ? (
                  <Close size={16} />
                ) : (
                  <Maximize size={16} />
                )}
              </button>
            </div>
          }
          footer={
            <div className={styles.chartFooter}>
              <div className={styles.footerGroup}>
                <span className={styles.footerLabel}>X-axis:</span>
                <XAxisSwitcher
                  chartType="cumulative"
                  value={volumeXAxisType}
                  onChange={handleVolumeXAxisChange}
                  className={styles.footerDropdown}
                  excludeOptions={['date']}
                  direction="top"
                />
              </div>
            </div>
          }
        />
      </div>

      {/* ── Bottom-right: Ratio (tabs in header) ── */}
      <div
        className={classNames(styles.chartCell, {
          [styles.chartCellHidden]: expandedChart && expandedChart !== 'ratio',
        })}
      >
        <MultiWellVolumeChart
          type="ratio"
          wells={productionWells}
          typeCurveData={typeCurveData}
          showTypeCurve={showTc}
          aggregationData={aggData}
          showAggregation={!!aggData}
          aggregationLabel={aggLabel}
          wellCountData={wellCounts}
          showWellCount={!!wellCounts}
          isLoading={productionLoading || forecastLoading}
          selectedSeries={selectedRatioSeries}
          highlightedUwi={highlightedUwi}
          onWellHover={handleWellHover}
          xAxisType={ratioXAxisType}
          xLinearMax={ratioXLinearMax}
          autoZoomX
          autoZoomY
          seriesStyle={ratioSeriesStyle}
          volumeDisplayMode={volumeDisplayMode}
          forecastData={forecastData}
          showForecast={showForecast}
          header={
            <div className={styles.chartTitleRow}>
              {RATIO_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={classNames(styles.chartTab, {
                    [styles.chartTabActive]: selectedRatio === opt.id,
                  })}
                  onClick={() => setSelectedRatio(opt.id)}
                >
                  {opt.text}
                </button>
              ))}
              <button
                type="button"
                className={styles.chartExpandButton}
                onClick={() =>
                  setExpandedChart((prev) => {
                    return prev === 'ratio' ? null : 'ratio';
                  })
                }
                aria-label={
                  expandedChart === 'ratio'
                    ? 'Collapse ratio chart'
                    : 'Expand ratio chart'
                }
              >
                {expandedChart === 'ratio' ? (
                  <Close size={16} />
                ) : (
                  <Maximize size={16} />
                )}
              </button>
            </div>
          }
          footer={
            <div className={styles.chartFooter}>
              <div className={styles.footerGroup}>
                <span className={styles.footerLabel}>X-axis:</span>
                <XAxisSwitcher
                  chartType="ratio"
                  value={ratioXAxisType}
                  onChange={handleRatioXAxisChange}
                  className={styles.footerDropdown}
                  excludeOptions={['date']}
                  direction="top"
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};
